import {
  computeBounds,
  isTileWithinBounds,
  tileDistanceFromBounds,
  isWalkable,
  Tile,
  WalkabilityGrid
} from '../../../../../services/spawnPointGeometry';

/**
 * A room-arrival spawn point is a "door" placed just outside the room it opens into. Everything
 * past this many tiles from the room's own bounds is a soft warning, not a rejection — rooms are
 * painted freehand, so an admin may legitimately want a door a little further out (SC-393).
 */
export const MAX_TILES_OUTSIDE_ROOM = 4;

export interface RoomArrivalValidationResult {
  ok: boolean;
  /** Set when ok is false — the placement is rejected outright and must not be saved. */
  reason?: string;
  /** Set when ok is true but the placement looks questionable — save is still allowed. */
  warning?: string;
}

/**
 * Validates a room-arrival spawn point placement against the room's own painted tiles.
 *
 * The one rule that must be enforced, not just warned about: a room-arrival point placed
 * inside the room's own bounds would join that room's audio/video the instant someone
 * "arrives" there, defeating the entire purpose of a door — so that case is rejected outright.
 * Distance-from-room and walkability are secondary, best-effort checks and only ever warn.
 *
 * Cubicles are the exception (SC-422): a desk is joined by sitting in it, so its spawn point may
 * be inside, and the migration puts missing ones at the cubicle's center.
 */
export function validateRoomArrivalPlacement(
  tile: Tile,
  roomTiles: Tile[],
  walkabilityGrid?: WalkabilityGrid | null,
  options: { allowInside?: boolean } = {}
): RoomArrivalValidationResult {
  if (roomTiles.length === 0) {
    return {
      ok: false,
      reason: 'This room has no painted tiles yet, so a door placement cannot be validated against it.'
    };
  }

  const bounds = computeBounds(roomTiles);

  const inside = isTileWithinBounds(tile, bounds);

  // Hard rejection: a door inside the room defeats the point of a door.
  if (inside && !options.allowInside) {
    return {
      ok: false,
      reason:
        'A room-arrival door cannot be placed inside the room itself — arriving there would ' +
        'immediately join the room. Place it just outside the room boundary instead.'
    };
  }

  let warning: string | undefined;

  const distance = inside ? 0 : tileDistanceFromBounds(tile, bounds);
  if (distance > MAX_TILES_OUTSIDE_ROOM) {
    warning =
      `This door is ${distance} tile(s) from the room boundary (recommended: within ` +
      `${MAX_TILES_OUTSIDE_ROOM}). Players may not feel like they've arrived at the room.`;
  }

  // Best-effort only: there is no live tilemap collision query wired up at this call site yet,
  // so an unwalkable tile warns rather than blocks (isWalkable is permissive with no grid).
  if (!isWalkable(walkabilityGrid, tile.x, tile.y)) {
    const walkabilityWarning = 'This tile may not be walkable — the door may be unreachable for players.';
    warning = warning ? `${warning} ${walkabilityWarning}` : walkabilityWarning;
  }

  return { ok: true, warning };
}
